import { useState, useEffect } from 'react';
import SectionHeader from '../../components/admin/SectionHeader';
import DataTable from '../../components/admin/DataTable';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Badge from '../../components/admin/Badge';

function AdminOffers({ apiFetch, token, toast, subscriptions, selectedSubs, setSelectedSubs }) {
  const [offerView, setOfferView] = useState('coupons');
  const [coupons, setCoupons] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [offerLoading, setOfferLoading] = useState(false);
  const [newCoupon, setNewCoupon] = useState({ name: '', type: 'percent', amount: '', duration: 'once', duration_in_months: '', redeem_by: '' });
  const [newPromo, setNewPromo] = useState({ coupon: '', code: '', max_redemptions: '', expires_at: '' });
  const [applyAction, setApplyAction] = useState('coupon');
  const [applyMonths, setApplyMonths] = useState(1);
  const [applyCouponId, setApplyCouponId] = useState('');
  const [applyRunning, setApplyRunning] = useState(false);
  const [applyResults, setApplyResults] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadOffersData = async () => {
    setOfferLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([apiFetch('/api/admin/coupons'), apiFetch('/api/admin/promo-codes')]);
      setCoupons(await cRes.json());
      setPromoCodes(await pRes.json());
    } catch { toast.error('Error', 'Failed to load offers data'); }
    finally { setOfferLoading(false); }
  };

  useEffect(() => { loadOffersData(); }, []);

  const createCoupon = async () => {
    try {
      const body = { ...newCoupon };
      if (body.redeem_by) {
        body.redeem_by = Math.floor(new Date(body.redeem_by + 'T23:59:59Z').getTime() / 1000);
        if (body.redeem_by <= Math.floor(Date.now() / 1000)) { toast.error('Error', 'Expiry date must be in the future'); return; }
      }
      const res = await apiFetch('/api/admin/coupons', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Created', 'Coupon created successfully');
      setNewCoupon({ name: '', type: 'percent', amount: '', duration: 'once', duration_in_months: '', redeem_by: '' });
      await loadOffersData();
    } catch (err) { toast.error('Error', err.message); }
  };

  const deleteCoupon = async () => {
    if (!deleteConfirm) return;
    try { await apiFetch(`/api/admin/coupons/${deleteConfirm.id}`, { method: 'DELETE' }); toast.success('Deleted', 'Coupon deleted'); await loadOffersData(); }
    catch (err) { toast.error('Error', err.message); }
    finally { setDeleteConfirm(null); }
  };

  const createPromoCode = async () => {
    try {
      const body = { ...newPromo };
      if (body.expires_at) body.expires_at = Math.floor(new Date(body.expires_at).getTime() / 1000);
      const res = await apiFetch('/api/admin/promo-codes', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error); }
      toast.success('Created', 'Promo code created');
      setNewPromo({ coupon: '', code: '', max_redemptions: '', expires_at: '' });
      await loadOffersData();
    } catch (err) { toast.error('Error', err.message); }
  };

  const deactivatePromoCode = async () => {
    if (!deleteConfirm) return;
    try { await apiFetch(`/api/admin/promo-codes/${deleteConfirm.id}/deactivate`, { method: 'POST' }); toast.success('Deactivated', 'Promo code deactivated'); await loadOffersData(); }
    catch (err) { toast.error('Error', err.message); }
    finally { setDeleteConfirm(null); }
  };

  const runApplyAction = async () => {
    if (selectedSubs.size === 0) { toast.warning('Warning', 'No subscriptions selected'); return; }
    setApplyRunning(true); setApplyResults(null);
    try {
      let url, body;
      if (applyAction === 'coupon') {
        if (!applyCouponId) { toast.warning('Warning', 'Select a coupon first'); setApplyRunning(false); return; }
        url = '/api/admin/offers/apply-coupon'; body = { subscriptionIds: Array.from(selectedSubs), couponId: applyCouponId };
      } else if (applyAction === 'pause') {
        url = '/api/admin/offers/pause-payments'; body = { subscriptionIds: Array.from(selectedSubs), months: applyMonths };
      } else {
        url = '/api/admin/offers/resume-payments'; body = { subscriptionIds: Array.from(selectedSubs) };
      }
      const res = await apiFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      setApplyResults(data.results);
      const ok = data.results.filter(r => r.success).length;
      toast.success('Done', `${ok}/${data.results.length} succeeded`);
    } catch (err) { toast.error('Error', err.message); }
    finally { setApplyRunning(false); }
  };

  const couponColumns = [
    { key: 'name', header: 'Name', render: row => <span style={{ fontWeight: '600' }}>{row.name || row.id}</span> },
    { key: 'type', header: 'Type', render: row => row.percent_off ? 'Percent' : 'Fixed' },
    { key: 'amount', header: 'Amount', render: row => row.percent_off ? `${row.percent_off}%` : `$${(row.amount_off / 100).toFixed(2)}` },
    { key: 'duration', header: 'Duration', render: row => row.duration === 'repeating' ? `${row.duration_in_months}mo` : row.duration },
    { key: 'redeemed', header: 'Redeemed', render: row => `${row.times_redeemed || 0}${row.max_redemptions ? `/${row.max_redemptions}` : ''}` },
    { key: 'expires', header: 'Expires', render: row => row.redeem_by ? new Date(row.redeem_by * 1000).toLocaleDateString() : '—' },
    { key: 'actions', header: 'Actions', render: row => <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => setDeleteConfirm({ type: 'coupon', id: row.id })}>Delete</button> },
  ];

  const promoColumns = [
    { key: 'code', header: 'Code', render: row => <span style={{ fontWeight: '700', fontFamily: 'monospace' }}>{row.code}</span> },
    { key: 'coupon', header: 'Coupon', render: row => row.coupon?.name || row.coupon?.id || '—' },
    { key: 'active', header: 'Active', render: row => <Badge variant={row.active ? 'active' : 'suspended'}>{row.active ? 'Yes' : 'No'}</Badge> },
    { key: 'max', header: 'Max Uses', render: row => row.max_redemptions || '—' },
    { key: 'used', header: 'Used', render: row => row.times_redeemed || 0 },
    { key: 'expires', header: 'Expires', render: row => row.expires_at ? new Date(row.expires_at * 1000).toLocaleDateString() : '—' },
    { key: 'actions', header: 'Actions', render: row => row.active ? <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => setDeleteConfirm({ type: 'promo', id: row.id })}>Deactivate</button> : null },
  ];

  return (
    <div>
      <SectionHeader title="Offers & Coupons" />

      <div className="admin-tabs" style={{ marginBottom: '16px' }}>
        {[{ key: 'coupons', label: 'Coupons' }, { key: 'promoCodes', label: 'Promo Codes' }, { key: 'apply', label: `Apply to Selected${selectedSubs.size > 0 ? ` (${selectedSubs.size})` : ''}` }].map(v => (
          <button key={v.key} className={`admin-tab ${offerView === v.key ? 'admin-tab--active' : ''}`} onClick={() => setOfferView(v.key)}>{v.label}</button>
        ))}
      </div>

      {/* Coupons tab */}
      {offerView === 'coupons' && (
        <div className="admin-card">
          <div className="admin-card" style={{ background: 'var(--admin-elevated)', marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', color: 'var(--admin-text-primary)', fontSize: '13px', fontWeight: '700' }}>Create Coupon</h4>
            <div className="admin-grid-3" style={{ marginBottom: '10px' }}>
              <div><label className="admin-label">NAME</label><input className="admin-input" type="text" value={newCoupon.name} placeholder="e.g. Ramadan 2026" onChange={e => setNewCoupon(p => ({ ...p, name: e.target.value }))} /></div>
              <div><label className="admin-label">TYPE</label><select className="admin-select" value={newCoupon.type} onChange={e => setNewCoupon(p => ({ ...p, type: e.target.value }))}><option value="percent">% Off</option><option value="fixed">$ Off</option></select></div>
              <div><label className="admin-label">AMOUNT</label><input className="admin-input" type="number" value={newCoupon.amount} placeholder={newCoupon.type === 'percent' ? '20' : '500 (cents)'} onChange={e => setNewCoupon(p => ({ ...p, amount: e.target.value }))} /></div>
              <div><label className="admin-label">DURATION</label><select className="admin-select" value={newCoupon.duration} onChange={e => setNewCoupon(p => ({ ...p, duration: e.target.value }))}><option value="once">Once</option><option value="repeating">Repeating</option><option value="forever">Forever</option></select></div>
              {newCoupon.duration === 'repeating' && <div><label className="admin-label">MONTHS</label><input className="admin-input" type="number" value={newCoupon.duration_in_months} placeholder="3" onChange={e => setNewCoupon(p => ({ ...p, duration_in_months: e.target.value }))} /></div>}
              <div><label className="admin-label">EXPIRY DATE</label><input className="admin-input" type="date" value={newCoupon.redeem_by} min={new Date().toISOString().split('T')[0]} onChange={e => setNewCoupon(p => ({ ...p, redeem_by: e.target.value }))} /></div>
            </div>
            <button className="admin-btn admin-btn--primary" onClick={createCoupon} disabled={!newCoupon.name || !newCoupon.amount}>Create Coupon</button>
          </div>
          <DataTable columns={couponColumns} data={coupons} loading={offerLoading} emptyMessage="No coupons yet" emptyIcon="fa-solid fa-ticket" />
        </div>
      )}

      {/* Promo Codes tab */}
      {offerView === 'promoCodes' && (
        <div className="admin-card">
          <div className="admin-card" style={{ background: 'var(--admin-elevated)', marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0', color: 'var(--admin-text-primary)', fontSize: '13px', fontWeight: '700' }}>Create Promo Code</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div><label className="admin-label">CODE</label><input className="admin-input" type="text" value={newPromo.code} placeholder="e.g. RAMADAN25" onChange={e => setNewPromo(p => ({ ...p, code: e.target.value.toUpperCase() }))} style={{ fontFamily: 'monospace' }} /></div>
              <div><label className="admin-label">COUPON</label><select className="admin-select" value={newPromo.coupon} onChange={e => setNewPromo(p => ({ ...p, coupon: e.target.value }))}><option value="">-- Select --</option>{coupons.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}</select></div>
              <div><label className="admin-label">MAX USES</label><input className="admin-input" type="number" value={newPromo.max_redemptions} placeholder="infinity" onChange={e => setNewPromo(p => ({ ...p, max_redemptions: e.target.value }))} /></div>
              <div><label className="admin-label">EXPIRY</label><input className="admin-input" type="date" value={newPromo.expires_at} onChange={e => setNewPromo(p => ({ ...p, expires_at: e.target.value }))} /></div>
            </div>
            <button className="admin-btn admin-btn--primary" onClick={createPromoCode} disabled={!newPromo.code || !newPromo.coupon}>Create Promo Code</button>
          </div>
          <DataTable columns={promoColumns} data={promoCodes} loading={offerLoading} emptyMessage="No promo codes yet" emptyIcon="fa-solid fa-barcode" />
        </div>
      )}

      {/* Apply to Selected tab */}
      {offerView === 'apply' && (
        <div className="admin-card">
          {selectedSubs.size === 0 ? (
            <p style={{ color: 'var(--admin-text-secondary)', margin: '0 0 16px 0' }}>No subscriptions selected. Check boxes in the Subscriptions table to select subscriptions.</p>
          ) : (
            <p style={{ color: 'var(--admin-accent)', fontWeight: '600', margin: '0 0 16px 0' }}>{selectedSubs.size} subscription{selectedSubs.size > 1 ? 's' : ''} selected</p>
          )}
          <div className="admin-tabs" style={{ marginBottom: '16px' }}>
            {[{ key: 'coupon', label: 'Apply Coupon' }, { key: 'pause', label: 'Pause Payments' }, { key: 'resume', label: 'Resume Payments' }].map(a => (
              <button key={a.key} className={`admin-tab ${applyAction === a.key ? 'admin-tab--active' : ''}`} onClick={() => setApplyAction(a.key)}>{a.label}</button>
            ))}
          </div>

          {applyAction === 'coupon' && (
            <div style={{ marginBottom: '16px' }}>
              <label className="admin-label">SELECT COUPON</label>
              <select className="admin-select" value={applyCouponId} onChange={e => setApplyCouponId(e.target.value)} style={{ minWidth: '240px' }}>
                <option value="">-- Select coupon --</option>
                {coupons.map(c => <option key={c.id} value={c.id}>{c.name || c.id} ({c.percent_off ? `${c.percent_off}%` : `$${(c.amount_off/100).toFixed(2)}`} {c.duration})</option>)}
              </select>
            </div>
          )}
          {applyAction === 'pause' && (
            <div style={{ marginBottom: '16px' }}>
              <label className="admin-label">MONTHS TO PAUSE</label>
              <select className="admin-select" value={applyMonths} onChange={e => setApplyMonths(parseInt(e.target.value))}>
                {[1,2,3,6,12].map(m => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
              </select>
              <p style={{ margin: '8px 0 0 0', color: 'var(--admin-text-secondary)', fontSize: '12px' }}>Invoices will be voided during the pause. Customer retains full access.</p>
            </div>
          )}
          {applyAction === 'resume' && <p style={{ color: 'var(--admin-text-secondary)', fontSize: '13px', margin: '0 0 16px 0' }}>Clears pause_collection from selected subscriptions. Billing resumes normally at next cycle.</p>}

          <button className="admin-btn admin-btn--primary" onClick={runApplyAction} disabled={applyRunning || selectedSubs.size === 0}>
            {applyRunning ? 'Running...' : `Apply to ${selectedSubs.size} subscription${selectedSubs.size !== 1 ? 's' : ''}`}
          </button>

          {applyResults && (
            <DataTable
              columns={[
                { key: 'email', header: 'Email', render: row => row.email || row.id },
                { key: 'plan', header: 'Plan', cellStyle: { color: 'var(--admin-text-secondary)' }, render: row => row.planName || '—' },
                { key: 'result', header: 'Result', render: row => row.success ? <span style={{ color: 'var(--admin-success)', fontWeight: '700' }}><i className="fa-solid fa-check" style={{marginRight:4}} /> Success</span> : <span style={{ color: '#f87171' }}><i className="fa-solid fa-xmark" style={{marginRight:4}} /> {row.error || 'Failed'}</span> },
              ]}
              data={applyResults}
            />
          )}
        </div>
      )}

      {/* Selection banner */}
      {selectedSubs.size > 0 && (
        <div className="admin-selection-banner">
          <span><i className="fa-solid fa-check" style={{marginRight:4}} /> {selectedSubs.size} subscription{selectedSubs.size > 1 ? 's' : ''} selected</span>
          <button className="admin-btn admin-btn--sm admin-btn--ghost" style={{ color: '#fff' }} onClick={() => setSelectedSubs(new Set())}>Clear</button>
          <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => setOfferView('apply')}><i className="fa-solid fa-arrow-right" style={{marginRight:4}} /> Apply Action</button>
        </div>
      )}

      {/* Delete/Deactivate Confirm */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={deleteConfirm?.type === 'coupon' ? deleteCoupon : deactivatePromoCode}
        title={deleteConfirm?.type === 'coupon' ? 'Delete Coupon' : 'Deactivate Promo Code'}
        message={deleteConfirm?.type === 'coupon' ? 'Are you sure you want to delete this coupon?' : 'Are you sure you want to deactivate this promo code?'}
        confirmText={deleteConfirm?.type === 'coupon' ? 'Delete' : 'Deactivate'}
        confirmVariant="danger"
      />
    </div>
  );
}

export default AdminOffers;
